import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsAPI, categoriesAPI } from '../../services/api';

interface Product {
  id: number;
  name: string;
  description: string;
  sku: string;
  categoryId: number;
  subcategoryId?: number;
  brand: string;
  retailPrice: number;
  retailerPrice: number;
  distributorPrice: number;
  quantity: number;
  isActive: boolean;
  isFeatured: boolean;
  images?: any[];
}

interface Category {
  id: number;
  name: string;
  subcategories?: Category[];
}

const AdminProducts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortDescription: '',
    sku: '',
    categoryId: '',
    subcategoryId: '',
    brand: '',
    retailPrice: '',
    retailCompareAtPrice: '',
    retailerPrice: '',
    retailerMinQuantity: '1',
    distributorPrice: '',
    distributorMinQuantity: '1',
    quantity: '0',
    isActive: true,
    isFeatured: false,
    images: [] as string[]
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    // Check if we should open create modal from URL parameter
    if (searchParams.get('create') === 'true') {
      setFormData({
        name: '',
        description: '',
        shortDescription: '',
        sku: '',
        categoryId: '',
        subcategoryId: '',
        brand: '',
        retailPrice: '',
        retailCompareAtPrice: '',
        retailerPrice: '',
        retailerMinQuantity: '1',
        distributorPrice: '',
        distributorMinQuantity: '1',
        quantity: '0',
        isActive: true,
        isFeatured: false,
        images: []
      });
      setEditingProduct(null);
      setShowModal(true);
      setSearchParams({}); // Clear the parameter
    }
  }, [searchParams, setSearchParams]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getProducts({ limit: 100 });
      setProducts(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching products:', error);
      alert(error.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getCategories();
      setCategories(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        categoryId: parseInt(formData.categoryId),
        subcategoryId: formData.subcategoryId ? parseInt(formData.subcategoryId) : null,
        retailPrice: parseFloat(formData.retailPrice),
        retailCompareAtPrice: formData.retailCompareAtPrice ? parseFloat(formData.retailCompareAtPrice) : null,
        retailerPrice: parseFloat(formData.retailerPrice),
        retailerMinQuantity: parseInt(formData.retailerMinQuantity),
        distributorPrice: parseFloat(formData.distributorPrice),
        distributorMinQuantity: parseInt(formData.distributorMinQuantity),
        quantity: parseInt(formData.quantity),
        images: formData.images
      };

      if (editingProduct) {
        await productsAPI.updateProduct(editingProduct.id.toString(), submitData);
        alert('Product updated successfully');
      } else {
        await productsAPI.createProduct(submitData);
        alert('Product created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error: any) {
      console.error('Error saving product:', error);
      const errorMsg = error.response?.data?.errors?.[0]?.msg || error.response?.data?.message || 'Failed to save product';
      alert(errorMsg);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      shortDescription: (product as any).shortDescription || '',
      sku: product.sku,
      categoryId: product.categoryId.toString(),
      subcategoryId: product.subcategoryId?.toString() || '',
      brand: product.brand,
      retailPrice: product.retailPrice.toString(),
      retailCompareAtPrice: ((product as any).retailCompareAtPrice || '').toString(),
      retailerPrice: product.retailerPrice.toString(),
      retailerMinQuantity: ((product as any).retailerMinQuantity || 1).toString(),
      distributorPrice: product.distributorPrice.toString(),
      distributorMinQuantity: ((product as any).distributorMinQuantity || 1).toString(),
      quantity: product.quantity.toString(),
      isActive: product.isActive,
      isFeatured: product.isFeatured,
      images: product.images || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await productsAPI.deleteProduct(id.toString());
      alert('Product deleted successfully');
      fetchProducts();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      alert(error.response?.data?.message || 'Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      shortDescription: '',
      sku: '',
      categoryId: '',
      subcategoryId: '',
      brand: '',
      retailPrice: '',
      retailCompareAtPrice: '',
      retailerPrice: '',
      retailerMinQuantity: '1',
      distributorPrice: '',
      distributorMinQuantity: '1',
      quantity: '0',
      isActive: true,
      isFeatured: false,
      images: []
    });
    setEditingProduct(null);
  };

  const getSubcategories = () => {
    if (!formData.categoryId) return [];
    const category = categories.find(c => c.id.toString() === formData.categoryId);
    return category?.subcategories || [];
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Products Management</h1>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            + Create Product
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">No products found</td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.sku}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.brand}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${product.retailPrice}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.quantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">{editingProduct ? 'Edit Product' : 'Create Product'}</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">SKU *</label>
                    <input
                      type="text"
                      required
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description *</label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category *</label>
                    <select
                      required
                      value={formData.categoryId}
                      onChange={(e) => {
                        setFormData({ ...formData, categoryId: e.target.value, subcategoryId: '' });
                      }}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subcategory</label>
                    <select
                      value={formData.subcategoryId}
                      onChange={(e) => setFormData({ ...formData, subcategoryId: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">None</option>
                      {getSubcategories().map((subcat) => (
                        <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Brand *</label>
                  <input
                    type="text"
                    required
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Retail Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.retailPrice}
                      onChange={(e) => setFormData({ ...formData, retailPrice: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Retailer Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.retailerPrice}
                      onChange={(e) => setFormData({ ...formData, retailerPrice: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                    {formData.retailPrice && formData.retailerPrice && parseFloat(formData.retailPrice) > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Discount: {(
                          ((parseFloat(formData.retailPrice) - parseFloat(formData.retailerPrice)) / 
                           parseFloat(formData.retailPrice)) * 100
                        ).toFixed(2)}%
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Distributor Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.distributorPrice}
                      onChange={(e) => setFormData({ ...formData, distributorPrice: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                    {formData.retailPrice && formData.distributorPrice && parseFloat(formData.retailPrice) > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Discount: {(
                          ((parseFloat(formData.retailPrice) - parseFloat(formData.distributorPrice)) / 
                           parseFloat(formData.retailPrice)) * 100
                        ).toFixed(2)}%
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                    <input
                      type="number"
                      required
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div className="flex items-center space-x-4 pt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="mr-2"
                      />
                      Active
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isFeatured}
                        onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                        className="mr-2"
                      />
                      Featured
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingProduct ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
